// @ts-nocheck

import clsx from "clsx";
import { type ComponentProps, useCallback, useMemo, useState } from "react";

import { useViewport } from "@widgets/device-info";

import { HistoryCard } from "@features/history-card/ui";
import type { MediaPostType } from "@features/media-card/model";
import { MediaCard } from "@features/media-card/ui";
import { MediaPopup } from "@features/media-popup";

import type { ColorIconType } from "@shared/content-types/types";
import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import { Popup } from "@shared/ui/popup";
import { Body, Heading } from "@shared/ui/typography";
import { getMediaType } from "@shared/utils/get-media-type";

import s from "./home-grid-cards.module.scss";

export type HomeGridCardsProps = ComponentProps<"div"> & {
  className?: string;
  media_posts: MediaPostType[];
  title: string;
  subtitle: string;
  pageIcon: ColorIconType;
};

export const HomeGridCards = (props: HomeGridCardsProps) => {
  const { className, media_posts, title, subtitle, pageIcon } = props;

  const { isDesktop } = useViewport();

  const [isViewAllPost, setViewAllPost] = useState(false);
  const [isOpenPopup, setOpenPopup] = useState(false);
  const [currentMediaId, setCurrentMediaId] = useState("");

  const handleClickOnCard = useCallback(
    (id: string) => {
      setCurrentMediaId(id);
      setOpenPopup(true);
    },
    [media_posts],
  );

  const onlyMediaPosts = useMemo(() => {
    return media_posts?.filter((post) => post.mediaContent);
  }, [media_posts]);

  const viewMorePosts = useCallback(() => {
    setViewAllPost(true);
  }, []);

  const currentPosts = useMemo(() => {
    if (!media_posts || media_posts?.length === 0) return [];

    if (isViewAllPost || isDesktop) {
      return media_posts;
    }

    return [...media_posts].slice(0, 3);
  }, [isViewAllPost, isDesktop, media_posts]);

  return (
    <>
      <section className={clsx(s.root, className)}>
        <div className={s.wrapper}>
          {currentPosts.map((post, index) => {
            const isVideo =
              getMediaType(post.mediaContent?.media?.mime) === "video";

            return post.isMedia ? (
              <MediaCard
                key={`media-card-${post.documentId}-${index + 1}`}
                className={clsx(s.block, {
                  [s.video]: isVideo,
                })}
                post={post}
                isVideo={isVideo}
                onCardClick={handleClickOnCard}
              />
            ) : (
              <HistoryCard
                key={`text-card-${post.documentId}-${index + 1}`}
                className={s.block}
                post={post.textContent}
              />
            );
          })}
          <div className={s.textBlock}>
            <Body
              size="uppercase-small"
              weight="semiBold"
              className={s.subtitle}
            >
              {subtitle}
            </Body>
            <Heading level={3} tag="h2">
              {title}
            </Heading>
          </div>
          <div className={s.emptyBlock} />
        </div>
        {!isViewAllPost && pageIcon && (
          <ButtonWithIcon
            onClick={viewMorePosts}
            variant="primary-dark"
            iconFillColor={pageIcon.fillColor}
            iconStrokeColor={pageIcon.strokeColor}
            iconName={pageIcon.icon?.name ?? "plus"}
            iconSvgCode={pageIcon.uicon?.svg_code}
            className={s.button}
          >
            смотреть больше
          </ButtonWithIcon>
        )}
      </section>
      <Popup
        id={"media-card-popup"}
        onClose={() => setOpenPopup(false)}
        isOpen={isOpenPopup}
        variant="media"
      >
        <MediaPopup
          media={onlyMediaPosts}
          currentId={currentMediaId}
          isOpen={isOpenPopup}
        />
      </Popup>
    </>
  );
};

HomeGridCards.displayName = "HomeGridCards";
